import React from 'react';
import Link from 'next/link';

const TOOL_LINKS = [
  { label: 'Image Compressor', href: '/tools/image-compress' },
  { label: 'Merge PDF', href: '/tools/merge-pdf' },
  { label: 'Background Remover', href: '/tools/background-remover' },
  { label: 'QR Generator', href: '/tools/qr-generator' },
  { label: 'Extract Text OCR', href: '/tools/extract-text-ocr' },
  { label: 'Audio Trimmer', href: '/tools/audio-trimmer' },
];

const COMPANY_LINKS = [
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms of Service', href: '/terms' },
];

export default function Footer() {
  return (
    <footer
      className="mt-16 relative z-10"
      style={{
        background: 'rgba(255,255,255,0.62)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderTop: '1px solid rgba(255,255,255,0.55)',
        boxShadow: '0 -4px 32px rgba(37,99,235,0.05)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10">
          {/* Brand */}
          <div className="sm:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <div
                className="w-9 h-9 rounded-2xl flex items-center justify-center shadow-md"
                style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 60%, #7c3aed 100%)' }}
              >
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
                </svg>
              </div>
              <span className="font-display font-bold text-slate-800 text-lg tracking-tight">mSuperTools</span>
            </div>
            <p className="text-slate-500 text-sm leading-relaxed max-w-xs mb-4">
              Free everyday PDF, image, and audio tools. All processing happens locally in your browser — your files never leave your device.
            </p>
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-emerald-600"
              style={{
                background: 'rgba(16,185,129,0.10)',
                border: '1px solid rgba(16,185,129,0.20)',
              }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              Private by design
            </div>
          </div>

          {/* Popular Tools */}
          <div className="sm:text-center">
            <h4 className="font-bold text-slate-700 text-sm mb-4 tracking-wide uppercase text-[11px]">Popular Tools</h4>
            <ul className="space-y-2.5 inline-block text-left">
              {TOOL_LINKS?.map((link) => (
                <li key={link?.label}>
                  <Link
                    href={link?.href}
                    className="text-slate-500 text-sm hover:text-blue-600 transition-colors flex items-center gap-1.5 group"
                  >
                    <svg className="w-3 h-3 text-slate-300 group-hover:text-blue-400 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="text-left">
            <h4 className="font-bold text-slate-700 text-sm mb-4 tracking-wide uppercase text-[11px]">Company</h4>
            <ul className="space-y-2.5">
              {COMPANY_LINKS?.map((link) => (
                <li key={link?.label}>
                  <Link
                    href={link?.href}
                    className="text-slate-500 text-sm hover:text-blue-600 transition-colors flex items-center gap-1.5 group"
                  >
                    <svg className="w-3 h-3 text-slate-300 group-hover:text-blue-400 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links removed */}
        </div>

        {/* Bottom bar */}
        <div
          className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}
        >
          <p className="text-slate-400 text-xs">© 2026 mSuperTools. All rights reserved.</p>
          <div className="flex items-center gap-5 text-xs text-slate-400">
            <Link href="/about" className="hover:text-blue-600 transition-colors">About</Link>
            <Link href="/privacy" className="hover:text-blue-600 transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-blue-600 transition-colors">Terms</Link>
            <Link href="/contact" className="hover:text-blue-600 transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}